import { computed, watch, reactive, getCurrentInstance, ComponentInternalInstance } from '@vue/composition-api';
import { throttle } from 'lodash';

import i18n, { i18n_t } from '@/locales/i18n';
import storage from '@/renderer/scripts/utils/storage';
import store from '@/renderer/store';
import { IS_OPEN_IN_APPLE, IS_PRIVATE } from '@/common/constant/platform';
import { getUserRole } from '@/renderer/scripts/utils/UserRole';
import { getOfficialWebsiteUrl, getExpiredDays, formatSize } from '@/renderer/scripts/utils/tools';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import smh from '@/renderer/scripts/smh';
import { CONSTANT_URL } from '@/renderer/scripts/http';

function bigNumberAdd(str1: string, str2: string): string {
  // js的最大的取值是2^53,所以长度小于14数字计算不会出错
  if (str1.length <= 14 && str2.length <= 14) {
    return (Number(str1) + Number(str2)).toString();
  }
  if (str1.length > 14 || str2.length > 14) {
    // 使用bigInt计算
    return (BigInt(str1) + BigInt(str2)).toString();
  }
  return '0';
}

export default function handler() {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  let renewType = 'default';
  const tipData = reactive({
    recycleMsg: '',
    showRecycleTip: false,
    // 误删恢复的tips
    showRenewTip: false,
    // 续费的tips
    warningBtn: '',
    renewalMsg: '',
    theme: 'info',
  });
  const orgAvaPercent = computed(() => {
    const { orgSpace, totalFileSize } = store.getters;
    return 100 - (orgSpace === '0' ? 0 : +((totalFileSize / orgSpace) * 100).toFixed(2));
  });
  const methods = {
    async showHeaderAdvert(type: string, expiredDay?: number, reportSource?: string) {
      const { isInWeMeetApp, orgChannel, channelUpdated, orgChannelEdition } = store.getters;
      const limits = [20, 10, 5];
      tipData.showRenewTip = true;
      tipData.theme = 'warning';
      renewType = type;
      switch (type) {
        case 'space':
          if (reportSource === 'setting') {
            // 个人心中空间提示条的上报
            reporter.report('value_added', { space_show: 'personal_setting_top_bar' });
          } else {
            reporter.report('value_added', {
              space_show: 'space_warning_top_bar',
            });
          }
          for (let i = 0; i < limits.length; i++) {
            if (orgAvaPercent.value < limits[i]) {
              // 渠道体验版未升级文案文案
              if (orgChannel && !channelUpdated) {
                // ios/mac文案
                if (IS_OPEN_IN_APPLE) {
                  tipData.renewalMsg = i18n_t('【{orgChannelEdition}】空间存储预警', {
                    orgChannelEdition,
                  });
                  tipData.warningBtn = i18n_t('查看更多扩容方案>>');
                } else {
                  tipData.renewalMsg = i18n_t('【{orgChannelEdition}】空间不足预警：可用空间不足{slot1}%', {
                    orgChannelEdition,
                    slot1: limits[i],
                  });
                  tipData.warningBtn = i18n_t('马上升级');
                }
              } else {
                tipData.renewalMsg = i18n_t('空间存储预警：可使用空间已不足{slot1}%{slot2}', {
                  slot1: limits[i],
                  slot2: !vm.$store.getters.isPrivate ? i18n_t('，点击进行') : '',
                });
                tipData.warningBtn = i18n_t('购买');
              }
            }
          }
          break;
        case 'expired':
          reporter.report('value_added', {
            use_time_show: 'use_time_warning_top_bar',
          });
          // 渠道体验版未升级文案文案
          if (orgChannel && !channelUpdated) {
            if (IS_OPEN_IN_APPLE) {
              tipData.renewalMsg = i18n_t('【{orgChannelEdition}】到期告警：有效期仅剩 {slot1} 天', {
                orgChannelEdition,
                slot1: (expiredDay as number) + 1,
              });
              tipData.warningBtn = i18n_t('查看更多扩容方案>>');
            } else {
              tipData.renewalMsg = i18n_t('【{orgChannelEdition}】到期告警：有效期仅剩 {slot1} 天，点击进行', {
                orgChannelEdition,
                slot1: (expiredDay as number) + 1,
              });
              tipData.warningBtn = i18n_t('续费');
            }
          } else {
            tipData.renewalMsg = i18n_t('到期告警：有效期仅剩 {slot1} 天，点击进行', {
              slot1: (expiredDay as number) + 1,
            });
            const renew = IS_OPEN_IN_APPLE ? i18n_t('处理') : i18n_t('Special.续费');
            tipData.warningBtn = renew;
          }
          break;
        case 'flow':
          reporter.report('value_added', {
            flow_show: 'flow_warning_top_bar',
          });
          // 渠道体验版未升级文案文案
          if (orgChannel && !channelUpdated) {
            if (IS_OPEN_IN_APPLE) {
              tipData.renewalMsg = i18n_t('【{orgChannelEdition}】可用流量预警，', {
                orgChannelEdition,
              });
              tipData.warningBtn = i18n_t('查看更多扩容方案>>');
            } else {
              tipData.renewalMsg = i18n_t('【{orgChannelEdition}】流量不足预警：可用流量不足500M，', {
                orgChannelEdition,
              });
              tipData.warningBtn = i18n_t('马上升级');
            }
          } else {
            tipData.renewalMsg = i18n_t('流量不足预警：可用流量不足500M，');
            tipData.warningBtn = i18n_t('马上升级');
          }
          break;
        default:
          reporter.report('value_added', {
            multi_product_show: '',
          });
          tipData.renewalMsg = i18n_t('【公告】{slot1}已多端覆盖，', {
            slot1: isInWeMeetApp ? i18n_t('会议文件空间') : i18n_t('腾讯云企业网盘'),
          });
          tipData.warningBtn = i18n_t('马上查看体验>>');
          tipData.theme = 'info';
          break;
      }
    },
    async getFlowData(role: string) {
      let flag = false;
      // 私有化 & 普通用户不请求流量查询
      if (!store.getters.isPrivate && role !== 'User') {
        try {
          const { data } = await smh.userAPI.trafficUsageOverview();
          // 处理流量的计算
          if (data) {
            const { freeRemain, paidRemain } = data;
            const remainFormat = formatSize(bigNumberAdd(freeRemain.toString(), paidRemain.toString()));
            // 流量低于500M的时候flag为true
            flag = remainFormat.size < 500 && ['B', 'KB', 'MB'].includes(remainFormat.unit);
          }
        } catch (err) {}
      }
      return flag;
    },
    // 初始化头部提示展示
    initHeadbarTipShow: throttle(
      async () => {
        await store.dispatch('GetOrgInfo', store.getters.orgId);
        const userRole = store.getters.userRole || (await getUserRole());
        const flowFlag = await methods.getFlowData(userRole);
        // 获取存储判断是否关闭过这个增值提示
        const topFlag = storage.get<string[]>(storage.StoreKey.NOT_SHOW_TOP_ADVERT);
        const { orgChannel, isInWeMeetApp, expireTime, channelUpdated, isPrivate } = store.getters;
        tipData.showRenewTip = false;
        // 整个企业的空间低于20%提示用户
        if (orgAvaPercent.value < 20 && userRole !== 'User' && !topFlag.includes('space')) {
          methods.showHeaderAdvert('space');
          return;
        }
        // FIX: 公有云才需要判断企业有效期
        if (!isPrivate) {
          const expiredDay = getExpiredDays(expireTime);
          // 企业的有效期在大于0天并且小于7天
          if (expiredDay >= 0 && expiredDay < 7 && userRole !== 'User' && !topFlag.includes('expired')) {
            methods.showHeaderAdvert('expired', expiredDay);
            return;
          }
        }
        // 流量是否充足
        if (flowFlag && userRole !== 'User' && !topFlag.includes('flow')) {
          methods.showHeaderAdvert('flow');
          return;
        }
        // 上面条件都不满足，渠道体验版就展示默认的增值引导条
        if (isInWeMeetApp && orgChannel && !channelUpdated && !topFlag.includes('default')) {
          methods.showHeaderAdvert('default');
        }
      },
      1000,
      { leading: false },
    ),
    // 关闭头部消息提示（广告等）
    closeHeadbarTips(val: string) {
      if (val === 'top-advert') {
        tipData.showRenewTip = false;
        const res = storage.get<string[]>(storage.StoreKey.NOT_SHOW_TOP_ADVERT);
        if (!res.includes(renewType)) res.unshift(renewType);
        storage.set(storage.StoreKey.NOT_SHOW_TOP_ADVERT, res);
      } else if (val === 'recycle-advert') {
        tipData.showRecycleTip = false;
        storage.set(storage.StoreKey.NOT_SHOW_RECYCLE_ADVERT, true);
      }
    },
    // 点击增值的操作
    handlerBuy() {
      const { orgChannel, orgId, channelUpdated, instanceId } = store.getters;
      // 上报统计点
      if (renewType === 'default') {
        reporter.report('value_added', {
          multi_product_click: '',
        });
        dispatchRouter(`${getOfficialWebsiteUrl()}/download`);
        return;
      }
      if (renewType === 'space') {
        reporter.report('value_added', {
          space_click: 'space_warning_top_bar',
        });
      } else if (renewType === 'expired') {
        reporter.report('value_added', {
          use_time_click: 'use_time_warning_top_bar',
        });
      } else if (renewType === 'flow') {
        reporter.report('value_added', {
          flow_click: 'flow_warning_top_bar',
        });
      }
      // 跳转链接
      if (orgChannel && !channelUpdated) {
        dispatchRouter(`${CONSTANT_URL.channelBuyUrl}&orgId=${orgId}&instanceId=${instanceId}`);
        return;
      }
      dispatchRouter(CONSTANT_URL.consoleManagementUrl);
    },
  };

  // 需要注意采用数组监听对象的形式
  watch(
    [
      () => store.getters.editionFlag,
      () => store.getters.expiredTime,
      () => store.getters.availablePercent,
      () => store.getters.userRole,
    ],
    val => {
      methods.initHeadbarTipShow();
    },
    {
      immediate: true,
    },
  );
  watch(
    () => vm.$route.name,
    val => {
      // 看看是不是有主动关闭过广告，关闭就不再展示
      const recycleFlag = storage.get<boolean>(storage.StoreKey.NOT_SHOW_RECYCLE_ADVERT);
      // 在回收站路由中 && 未关闭消息提示
      if (val === 'recycleBin' && !recycleFlag) {
        switch (store.getters.editionFlag) {
          case 'personal':
            tipData.recycleMsg = i18n_t('个人版支持恢复3天内删除的文件，升级企业版可延长至90天');
            break;
          case 'team':
            tipData.recycleMsg = i18n_t('团队版支持恢复90天内删除的文件');
            break;
          default:
            tipData.recycleMsg = IS_PRIVATE
              ? i18n_t('支持恢复90天内删除的文件')
              : i18n_t('企业版支持恢复90天内删除的文件');
            break;
        }
        tipData.showRecycleTip = true; // 开启误删恢复tips
        tipData.showRenewTip = false; // 关闭续费tips
      } else {
        tipData.showRecycleTip = false;
        methods.initHeadbarTipShow();
      }
    },
  );
  return { methods, tipData, orgAvaPercent };
}
